import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Autocomplete,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  LocalShipping as LocalShippingIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  CreditCard as LicenceIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Template {
  template_id: string;
  template_name: string;
  description?: string | null;
  tags?: any;
  requires_section_6_8_waiver?: number | boolean | null;
  requires_whole_good_number?: number | boolean | null;
  whole_good_number_prefix?: string | null;
}

interface BusinessUser {
  id: string;
  user_id?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  role?: string | null;
}

type DispatchMode = 'in_app_user' | 'external_link';
type Direction = 'incoming' | 'outgoing';
type AssetSource = 'temporary' | 'existing';

interface AssetTypeRow {
  asset_type_id: string;
  allow_loan_attachments?: boolean | number | string;
}

interface SystemAsset {
  asset_id: string;
  asset_name: string;
  asset_type_id?: string;
  serial_number?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  is_out_of_action?: number | boolean;
  allow_loan_attachments?: boolean | number | string;
  job_tags?: { job_tag_id: string; tag_name: string; tag_scope?: string }[];
}

const flagIsOn = (v: boolean | number | string | undefined) =>
  v === true || v === 1 || v === '1';

const assetAllowsAttachments = (a: SystemAsset, typeById: Map<string, AssetTypeRow>) => {
  if (flagIsOn(a.allow_loan_attachments)) return true;
  if (!a.asset_type_id) return false;
  const t = typeById.get(a.asset_type_id);
  return t ? flagIsOn(t.allow_loan_attachments) : false;
};

const isAttachmentTagged = (a: SystemAsset) =>
  (a.job_tags ?? []).some(t => t.tag_name.toLowerCase() === 'attachments');

interface CreateDeliveryCheckModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

interface ResultPayload {
  driverLink?: string | null;
  expiresAt?: string | null;
  emailSent?: boolean;
  dvlaCheck?: { requestId: string; emailSent: boolean } | null;
}

const isDeliveryTemplate = (t: Template): boolean => {
  try {
    const raw = t.tags;
    if (!raw) return false;
    const tags = Array.isArray(raw) ? raw : JSON.parse(typeof raw === 'string' ? raw : JSON.stringify(raw));
    return Array.isArray(tags) && tags.map((x: any) => String(x).toLowerCase()).includes('delivery');
  } catch {
    return false;
  }
};

const normalizeBusinessUser = (raw: Record<string, unknown>): BusinessUser | null => {
  const idRaw = raw.user_id ?? raw.userId ?? raw.id;
  if (idRaw == null || idRaw === '') return null;
  const id = String(idRaw);
  const firstName = String(raw.first_name ?? raw.firstName ?? '').trim();
  const lastName = String(raw.last_name ?? raw.lastName ?? '').trim();
  const full_name =
    (typeof raw.full_name === 'string' && raw.full_name.trim()) ||
    `${firstName} ${lastName}`.trim() ||
    undefined;
  return {
    id,
    user_id: id,
    email: String(raw.email ?? ''),
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    full_name,
    role: (raw.role as string | null | undefined) ?? null,
  };
};

const fullName = (u: BusinessUser): string => {
  if (u.full_name) return u.full_name;
  const fn = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return fn || u.email;
};

const DEFAULT_WHOLE_GOOD_PREFIX = 'WGS';

const templateRequiresWholeGoodNumber = (t: Template | null): boolean =>
  !!t && (t.requires_whole_good_number === true || t.requires_whole_good_number === 1);

const getTemplateWholeGoodPrefix = (t: Template | null): string => {
  const extra = (t?.whole_good_number_prefix || '').trim().toUpperCase();
  const extraPart = (extra && extra !== DEFAULT_WHOLE_GOOD_PREFIX) ? extra : '';
  return `${DEFAULT_WHOLE_GOOD_PREFIX}${extraPart}`;
};

const stripWholeGoodPrefix = (value: string, prefix: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const normalizedPrefix = (prefix || DEFAULT_WHOLE_GOOD_PREFIX).trim().toUpperCase();
  if (trimmed.toUpperCase().startsWith(normalizedPrefix)) {
    return trimmed.slice(normalizedPrefix.length).replace(/^[\s-]+/, '').trim();
  }
  if (normalizedPrefix !== DEFAULT_WHOLE_GOOD_PREFIX && trimmed.toUpperCase().startsWith(DEFAULT_WHOLE_GOOD_PREFIX)) {
    return trimmed.slice(DEFAULT_WHOLE_GOOD_PREFIX.length).replace(/^[\s-]+/, '').trim();
  }
  return trimmed;
};

const normalizeWholeGoodForApi = (value: string, prefix: string): string | null => {
  const stripped = stripWholeGoodPrefix(value, prefix);
  return stripped || null;
};

const CreateDeliveryCheckModal: React.FC<CreateDeliveryCheckModalProps> = ({ open, onClose, onCreated }) => {
  // Asset fields
  const [wholeGoodNumber, setWholeGoodNumber] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [specification, setSpecification] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [recordedHours, setRecordedHours] = useState('');

  // Asset source (outgoing only)
  const [assetSource, setAssetSource] = useState<AssetSource>('temporary');
  const [assets, setAssets] = useState<SystemAsset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<SystemAsset | null>(null);

  // Templates / users
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState<string>('');
  const [users, setUsers] = useState<BusinessUser[]>([]);

  // Dispatch
  const [dispatchMode, setDispatchMode] = useState<DispatchMode>('in_app_user');
  const [direction, setDirection] = useState<Direction>('outgoing');
  const [assignedUserId, setAssignedUserId] = useState<string>('');
  const [externalName, setExternalName] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [externalPhone, setExternalPhone] = useState('');

  const [selectedAttachments, setSelectedAttachments] = useState<SystemAsset[]>([]);
  const [assetTypeById, setAssetTypeById] = useState<Map<string, AssetTypeRow>>(new Map());

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<ResultPayload | null>(null);
  const [copied, setCopied] = useState(false);
  const [dvlaEnabled, setDvlaEnabled] = useState(false);
  const [includeDvlaCheck, setIncludeDvlaCheck] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find(t => t.template_id === templateId) || null,
    [templates, templateId]
  );
  const selectedAssignee = useMemo(
    () => users.find(u => u.id === assignedUserId) ?? null,
    [users, assignedUserId]
  );
  const waiverRequiredByTemplate = !!(selectedTemplate?.requires_section_6_8_waiver);
  const wholeGoodRequiredByTemplate = templateRequiresWholeGoodNumber(selectedTemplate);
  const wholeGoodPrefix = getTemplateWholeGoodPrefix(selectedTemplate);

  /** Outgoing + existing asset only; show when the primary asset's type has allow_loan_attachments */
  const showAttachmentsSection = useMemo(() => {
    if (direction !== 'outgoing' || assetSource !== 'existing' || !selectedAsset) return false;
    return assetAllowsAttachments(selectedAsset, assetTypeById);
  }, [direction, assetSource, selectedAsset, assetTypeById]);

  useEffect(() => {
    if (!showAttachmentsSection) {
      setSelectedAttachments([]);
    }
  }, [showAttachmentsSection, selectedAsset?.asset_id]);

  const resetForm = () => {
    setWholeGoodNumber('');
    setManufacturer('');
    setModel('');
    setSpecification('');
    setSerialNumber('');
    setRegistrationNumber('');
    setRecordedHours('');
    setTemplateId('');
    setAssignedUserId('');
    setDispatchMode('in_app_user');
    setDirection('outgoing');
    setExternalName('');
    setExternalEmail('');
    setExternalPhone('');
    setErrorMessage(null);
    setSuccessResult(null);
    setCopied(false);
    setIncludeDvlaCheck(false);
    setAssetSource('temporary');
    setSelectedAsset(null);
    setSelectedAttachments([]);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setErrorMessage('No authentication token found');
          setLoading(false);
          return;
        }
        const [tplRes, usersRes, bizRes, assetsRes, typesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/check-templates`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { tag: 'delivery' },
          }).catch(() => null),
          axios.get(`${API_BASE_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => null),
          axios.get(`${API_BASE_URL}/businesses/current`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => null),
          axios.get(`${API_BASE_URL}/assets`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => null),
          axios.get(`${API_BASE_URL}/asset-types`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => null),
        ]);
        if (cancelled) return;
        if (tplRes?.data) {
          const list: Template[] = Array.isArray(tplRes.data)
            ? tplRes.data
            : (tplRes.data.data || tplRes.data.templates || []);
          const deliveryOnly = list.filter(isDeliveryTemplate);
          setTemplates(deliveryOnly);
          if (deliveryOnly.length === 1) setTemplateId(deliveryOnly[0].template_id);
        }
        if (usersRes?.data) {
          const list: unknown[] = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data.users || []);
          setUsers(
            list
              .map(u => normalizeBusinessUser(u as Record<string, unknown>))
              .filter((u): u is BusinessUser => u != null)
          );
        }
        if (bizRes?.data) {
          const biz = bizRes.data?.business || bizRes.data;
          const dvlaOn = !biz?.disable_dvla_checks && (biz?.dvla_credits ?? 0) > 0;
          setDvlaEnabled(!!dvlaOn);
        }
        let typeMap = new Map<string, AssetTypeRow>();
        if (typesRes?.data) {
          const types: AssetTypeRow[] = Array.isArray(typesRes.data)
            ? typesRes.data
            : (typesRes.data.data || typesRes.data.assetTypes || []);
          typeMap = new Map(types.map(t => [t.asset_type_id, t]));
          setAssetTypeById(typeMap);
        }
        if (assetsRes?.data) {
          const list: SystemAsset[] = assetsRes.data?.data || assetsRes.data || [];
          const active = list.filter((a) => !a.is_out_of_action);
          setAssets(active);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Failed to load create-delivery-check data:', err);
          setErrorMessage('Failed to load templates or users');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    const createdSuccessfully = !!successResult;
    resetForm();
    onClose();
    if (createdSuccessfully) {
      onCreated?.();
    }
  };

  const validate = (): string | null => {
    if (!templateId) return 'Please choose a delivery template.';
    if (direction === 'outgoing' && assetSource === 'existing' && !selectedAsset) {
      return 'Please select an asset from your asset list.';
    }
    if (dispatchMode === 'in_app_user' && !assignedUserId) return 'Please choose a user to complete the check.';
    if (dispatchMode === 'external_link') {
      if (!externalName.trim() || !externalEmail.trim()) {
        return 'Driver name and email are required for the external link.';
      }
    }
    if (wholeGoodRequiredByTemplate && (direction !== 'outgoing' || assetSource === 'temporary') && !wholeGoodNumber.trim()) {
      return `${wholeGoodPrefix} number is required for the selected template.`;
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setErrorMessage(err);
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const token = localStorage.getItem('token');
      const body: any = {
        templateId,
        direction,
        includeDvlaCheck: dispatchMode === 'external_link' && includeDvlaCheck,
        ...(direction === 'outgoing' && assetSource === 'existing' && selectedAsset
          ? { assetId: selectedAsset.asset_id }
          : {}),
        ...(selectedAttachments.length > 0
          ? { linked_asset_ids: selectedAttachments.map(a => a.asset_id) }
          : {}),
        tempAsset: {
          wholeGoodNumber: normalizeWholeGoodForApi(wholeGoodNumber, wholeGoodPrefix),
          manufacturer: manufacturer.trim() || null,
          model: model.trim() || null,
          specification: specification.trim() || null,
          serialNumber: serialNumber.trim() || null,
          registrationNumber: registrationNumber.trim() || null,
          recordedHours: recordedHours ? Number(recordedHours) : null,
        },
        dispatch: dispatchMode === 'in_app_user'
          ? { mode: 'in_app_user', userId: assignedUserId }
          : {
              mode: 'external_link',
              externalDriver: {
                name: externalName.trim(),
                email: externalEmail.trim(),
                phone: externalPhone.trim() || null,
              },
            },
      };
      const response = await axios.post(`${API_BASE_URL}/deliveries/temp-asset-check`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data;
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create delivery check');
      }
      if (dispatchMode === 'external_link' && data.dispatch?.driverLink) {
        setSuccessResult({
          driverLink: data.dispatch.driverLink,
          expiresAt: data.dispatch.expiresAt || null,
          emailSent: !!data.dispatch.emailSent,
          dvlaCheck: data.dispatch.dvlaCheck || null,
        });
      } else {
        setSuccessResult({ driverLink: null, expiresAt: null, emailSent: false, dvlaCheck: null });
      }
    } catch (e: any) {
      setErrorMessage(e?.response?.data?.error || e?.message || 'Failed to create delivery check');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!successResult?.driverLink) return;
    try {
      await navigator.clipboard.writeText(successResult.driverLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {successResult ? (
          <CheckCircleIcon color="success" />
        ) : (
          <LocalShippingIcon color="primary" />
        )}
        <Box sx={{ flex: 1 }}>
          {successResult ? 'Delivery check created' : 'New Delivery Check'}
        </Box>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : successResult ? (
          <Stack spacing={2}>
            <Typography variant="body1" color="text.secondary">
              Your delivery check was created successfully.
            </Typography>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              {dispatchMode === 'external_link'
                ? (successResult.emailSent
                    ? 'The driver has been emailed a link to complete the check.'
                    : 'A link has been generated below — copy it and send it to the driver if needed.')
                : 'The assigned user will see it in their Deliveries tab in the app.'}
            </Alert>
            {successResult.dvlaCheck && (
              <Alert severity="info" icon={<LicenceIcon />}>
                {successResult.dvlaCheck.emailSent
                  ? 'A driving licence check request has also been emailed to the driver.'
                  : 'A driving licence check request has been created (email delivery may have failed — check DVLA management).'}
              </Alert>
            )}
            {successResult.driverLink && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Driver Link
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    {successResult.driverLink}
                  </Box>
                </Box>
                {successResult.expiresAt && (
                  <Typography variant="caption" color="text.secondary">
                    Expires {new Date(successResult.expiresAt).toLocaleString()}
                  </Typography>
                )}
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyLink}
                  >
                    {copied ? 'Copied!' : 'Copy link'}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        ) : (
          <Stack spacing={3}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            {/* Direction toggle */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Direction
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={direction}
                onChange={(_e, val) => {
                  if (!val) return;
                  setDirection(val as Direction);
                  if (val === 'incoming') {
                    setAssetSource('temporary');
                    setSelectedAsset(null);
                  }
                }}
                size="small"
                color="primary"
              >
                <ToggleButton value="outgoing" sx={{ gap: 0.5 }}>
                  <ArrowForwardIcon fontSize="small" />
                  Outgoing
                </ToggleButton>
                <ToggleButton value="incoming" sx={{ gap: 0.5 }}>
                  <ArrowBackIcon fontSize="small" />
                  Incoming
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {direction === 'outgoing'
                  ? 'Asset is leaving your premises — driver drops off at the destination.'
                  : 'Asset is arriving at your premises — driver delivers to you.'}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Asset
              </Typography>
              {direction === 'outgoing' && (
                <>
                  <RadioGroup
                    row
                    value={assetSource}
                    onChange={(e) => {
                      const src = e.target.value as AssetSource;
                      setAssetSource(src);
                      if (src === 'temporary') setSelectedAsset(null);
                    }}
                    sx={{ mb: 2 }}
                  >
                    <FormControlLabel value="temporary" control={<Radio />} label="Temporary asset (not in asset list)" />
                    <FormControlLabel value="existing" control={<Radio />} label="Existing asset in system" />
                  </RadioGroup>
                  {assetSource === 'existing' && (
                    <Autocomplete
                      options={assets}
                      value={selectedAsset}
                      onChange={(_e, val) => {
                        setSelectedAsset(val);
                        setSelectedAttachments(prev =>
                          val ? prev.filter(a => a.asset_id !== val.asset_id) : prev
                        );
                      }}
                      getOptionLabel={(a) =>
                        [a.asset_name, a.serial_number ? `S/N ${a.serial_number}` : null]
                          .filter(Boolean)
                          .join(' · ')
                      }
                      isOptionEqualToValue={(a, b) => a.asset_id === b.asset_id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select asset"
                          required
                          placeholder="Search by name or serial number"
                        />
                      )}
                      sx={{ mb: 2 }}
                    />
                  )}
                </>
              )}
              {(direction !== 'outgoing' || assetSource === 'temporary') && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter the details of the item being delivered. It will not be added to your asset list.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={wholeGoodRequiredByTemplate ? 'Whole good number' : 'Whole good number (optional)'}
                        required={wholeGoodRequiredByTemplate}
                        value={wholeGoodNumber}
                        onChange={(e) => setWholeGoodNumber(stripWholeGoodPrefix(e.target.value, wholeGoodPrefix))}
                        placeholder="e.g. 12345"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="body2" fontWeight={700} color="text.secondary">
                                {wholeGoodPrefix}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          wholeGoodRequiredByTemplate
                            ? `Required. Enter the number only — saved as ${wholeGoodPrefix}<number>, e.g. ${wholeGoodPrefix}9293.`
                            : `Optional. Enter the number only — saved as ${wholeGoodPrefix}<number>, e.g. ${wholeGoodPrefix}9293.`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Manufacturer" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} placeholder="e.g. JCB" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Model" value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. 3CX" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Specification" value={specification} onChange={(e) => setSpecification(e.target.value)} placeholder="e.g. Backhoe loader, 4WS, AC" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Serial number" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Registration" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Recorded hours" type="number" value={recordedHours} onChange={(e) => setRecordedHours(e.target.value)} placeholder="0" />
                    </Grid>
                  </Grid>
                </>
              )}
              {direction === 'outgoing' && assetSource === 'existing' && selectedAsset && (
                <Alert severity={wholeGoodRequiredByTemplate ? 'warning' : 'info'} sx={{ mt: 1 }}>
                  Delivering <strong>{selectedAsset.asset_name}</strong>
                  {selectedAsset.serial_number ? ` (S/N ${selectedAsset.serial_number})` : ''}.
                  {wholeGoodRequiredByTemplate
                    ? ` This template requires a ${wholeGoodPrefix} number from the asset's custom fields.`
                    : ' Whole good number will be taken from the asset\'s custom fields if set.'}
                </Alert>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Delivery template
              </Typography>
              {templates.length === 0 ? (
                <Alert severity="info">
                  No delivery templates found. Tag a template with <strong>delivery</strong> in Template Management.
                </Alert>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="template-select-label">Template</InputLabel>
                  <Select
                    labelId="template-select-label"
                    label="Template"
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value as string)}
                  >
                    {templates.map(t => (
                      <MenuItem key={t.template_id} value={t.template_id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{t.template_name}</span>
                          {t.requires_section_6_8_waiver ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                              label="Section 6(8) waiver"
                              sx={{ height: 20 }}
                            />
                          ) : null}
                          {templateRequiresWholeGoodNumber(t) ? (
                            <Chip
                              size="small"
                              color="info"
                              variant="outlined"
                              label={`${getTemplateWholeGoodPrefix(t)} required`}
                              sx={{ height: 20 }}
                            />
                          ) : null}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {waiverRequiredByTemplate && (
                <Alert severity="warning" sx={{ mt: 1 }} icon={<WarningAmberIcon />}>
                  This template requires a Section 6(8) waiver signature before the check can be completed.
                </Alert>
              )}
              {wholeGoodRequiredByTemplate && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This template requires a {wholeGoodPrefix} number when creating the delivery check.
                </Alert>
              )}
            </Box>

            {/* Attachments — outgoing existing asset only; primary type must have allow_loan_attachments; list = "Attachments"-tagged assets */}
            {showAttachmentsSection && (() => {
              const primaryId = selectedAsset?.asset_id;
              const eligible = assets
                .filter(a => !a.is_out_of_action && a.asset_id !== primaryId && isAttachmentTagged(a));
              if (eligible.length === 0) return null;
              const displayed = eligible;
              return (
                <>
                  <Divider />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Attachments
                        {selectedAttachments.length > 0 && (
                          <Chip
                            label={selectedAttachments.length}
                            size="small"
                            color="primary"
                            sx={{ ml: 1, height: 20 }}
                          />
                        )}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Optional. Select additional assets tagged &quot;Attachments&quot; to include on this delivery check.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                      {displayed.map(a => {
                        const checked = selectedAttachments.some(x => x.asset_id === a.asset_id);
                        return (
                          <Box
                            key={a.asset_id}
                            onClick={() =>
                              setSelectedAttachments(prev =>
                                checked ? prev.filter(x => x.asset_id !== a.asset_id) : [...prev, a]
                              )
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1.5,
                              py: 1,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: checked ? 'primary.main' : 'divider',
                              bgcolor: checked ? 'primary.50' : 'background.paper',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: checked ? 'primary.50' : 'action.hover' },
                            }}
                          >
                            <Checkbox
                              checked={checked}
                              size="small"
                              sx={{ p: 0 }}
                              onClick={e => e.stopPropagation()}
                              onChange={() =>
                                setSelectedAttachments(prev =>
                                  checked ? prev.filter(x => x.asset_id !== a.asset_id) : [...prev, a]
                                )
                              }
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {a.asset_name}
                              </Typography>
                              {a.serial_number && (
                                <Typography variant="caption" color="text.secondary">
                                  S/N: {a.serial_number}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </>
              );
            })()}

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Dispatch
              </Typography>
              <RadioGroup
                row
                value={dispatchMode}
                onChange={(e) => setDispatchMode(e.target.value as DispatchMode)}
              >
                <FormControlLabel value="in_app_user" control={<Radio />} label="Assign to in-app user" />
                <FormControlLabel value="external_link" control={<Radio />} label="External driver (magic link)" />
              </RadioGroup>

              {dispatchMode === 'in_app_user' ? (
                <Autocomplete
                  sx={{ mt: 2 }}
                  options={users}
                  value={selectedAssignee}
                  onChange={(_e, val) => setAssignedUserId(val?.id ?? '')}
                  getOptionLabel={(u) =>
                    [fullName(u), u.email ? `(${u.email})` : null].filter(Boolean).join(' ')
                  }
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disableClearable
                  blurOnSelect
                  noOptionsText="No users available"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign to user"
                      required
                      placeholder="Search by name or email"
                    />
                  )}
                />
              ) : (
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Driver name" value={externalName} onChange={(e) => setExternalName(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required type="email" label="Driver email" value={externalEmail} onChange={(e) => setExternalEmail(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Driver phone (optional)" value={externalPhone} onChange={(e) => setExternalPhone(e.target.value)} />
                  </Grid>
                  {dvlaEnabled && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: includeDvlaCheck ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          bgcolor: includeDvlaCheck ? 'primary.50' : 'background.paper',
                          cursor: 'pointer',
                        }}
                        onClick={() => setIncludeDvlaCheck(v => !v)}
                      >
                        <Switch
                          checked={includeDvlaCheck}
                          onChange={(e) => setIncludeDvlaCheck(e.target.checked)}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LicenceIcon fontSize="small" color={includeDvlaCheck ? 'primary' : 'action'} />
                            <Typography variant="body2" fontWeight={600}>
                              Also request a driving licence check
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            The driver will receive a separate email asking them to submit their driving licence number for a DVLA check.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>{successResult ? 'Done' : 'Cancel'}</Button>
        {!successResult && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || loading || templates.length === 0}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            {submitting ? 'Creating…' : 'Create delivery check'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateDeliveryCheckModal;
